/**
 * Creates a promise that resolves after the specified delay.
 * Useful for introducing pauses in async operations.
 *
 * @param ms - The delay duration in milliseconds
 * @returns A promise that resolves after the specified delay
 *
 * @example
 * await delay(1000); // Wait for 1 second
 */
export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wraps a promise with a timeout that rejects if the promise doesn't resolve within the specified duration.
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout duration in milliseconds
 * @param errorMessage - Error message to use when timeout occurs
 * @returns The resolved value of the promise
 * @throws Error if the timeout is exceeded
 */
export const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  let timeoutId: NodeJS.Timeout;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId!);
  }
};

export const fetchWithTimeout = async (url: string, init: RequestInit, timeoutMs = 10_000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
};
